/**
 * Venda Canopus: escolhe o contato, completa o cadastro que a Canopus exige e
 * manda para o api-server (POST /canopus/venda). O envio de verdade (contrato e
 * boleto para o cliente) só acontece com "Enviar à Canopus" marcado e depois da
 * tela de confirmação; sem isso o pedido fica salvo como rascunho na Canopus.
 */
import { useEffect, useMemo, useState } from 'react';
import { useDataProvider, useGetList, useNotify } from 'ra-core';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Spinner } from '@/components/ui/spinner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatBRL } from '@/lib/format';
import { supabase } from '@/components/atomic-crm/providers/supabase/supabase';
import { venderCanopus, type ClienteCanopus, type VendaCanopusResultado } from '@/lib/canopus-api';
import type { Contact } from '../types';
import { getContactDetails, getContactDisplayName } from '../contacts/contact-details';
import { CanopusClienteCampos } from './CanopusClienteCampos';
import { chavesFaltando, clienteDoContato, COMPROVANTES_RENDA, extrasParaContato, faltando, FORMAS_CONTATO, jaTemFichaCanopus } from './venda-canopus';

export interface PlanoCanopus {
  codigo_bem: string;
  nome_bem: string;
  valor_credito: number;
  prazo_meses: number;
  primeira_parcela: number;
  tipo_venda: string;
  plano: string;
  id_tipo_produto: number;
}

export function CanopusVendaModal({ plano, aoFechar }: { plano: PlanoCanopus | null; aoFechar: () => void }) {
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const [busca, setBusca] = useState('');
  const [buscaAtrasada, setBuscaAtrasada] = useState('');
  const [contato, setContato] = useState<Contact | null>(null);
  const [cliente, setCliente] = useState<ClienteCanopus | null>(null);
  // O que pedir em "Informações complementares": calculado ao escolher o
  // contato (fixo, para os campos não sumirem enquanto se digita).
  const [pedir, setPedir] = useState<Set<string>>(new Set());
  const [revisarTudo, setRevisarTudo] = useState(false);
  const [formaContato, setFormaContato] = useState('4');
  const [comprovante, setComprovante] = useState('');
  const [enviarCanopus, setEnviarCanopus] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [andamento, setAndamento] = useState('');
  const [resultado, setResultado] = useState<VendaCanopusResultado | null>(null);
  const [erro, setErro] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setBuscaAtrasada(busca), 300);
    return () => clearTimeout(t);
  }, [busca]);

  useEffect(() => {
    if (!plano) return;
    setBusca(''); setContato(null); setCliente(null); setComprovante(''); setEnviarCanopus(false);
    setPedir(new Set()); setRevisarTudo(false);
    setConfirmando(false); setEnviando(false); setAndamento(''); setResultado(null); setErro('');
  }, [plano]);

  const { data: contatos } = useGetList<Contact>('contacts', {
    filter: buscaAtrasada.length >= 2 ? { q: buscaAtrasada } : { id: -999 },
    pagination: { page: 1, perPage: 20 },
    sort: { field: 'details->>name', order: 'ASC' },
  });

  const pendentes = useMemo(() => {
    if (!cliente) return ['contato'];
    const f = faltando(cliente);
    if (!comprovante) f.push('comprovante de renda');
    return f;
  }, [cliente, comprovante]);

  if (!plano) return null;

  const escolher = (c: Contact) => {
    const novo = clienteDoContato(c);
    setContato(c); setCliente(novo); setBusca(''); setRevisarTudo(false);
    setPedir(chavesFaltando(novo, jaTemFichaCanopus(c)));
  };

  const enviar = async () => {
    if (!cliente) return;
    setEnviando(true); setErro(''); setAndamento('Enviando para o servidor…');
    try {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token) throw new Error('Sessão expirada. Entre novamente no sistema.');
      const r = await venderCanopus({
        cliente: { ...cliente, cpf: cliente.cpf.replace(/\D/g, '') },
        plano: { codigo: plano.codigo_bem, valor: Number(plano.valor_credito), prazo: Number(plano.prazo_meses), tipo: plano.tipo_venda, produto: plano.id_tipo_produto },
        pedido: { forma_contato: Number(formaContato), comprovante_renda: Number(comprovante) },
        finalizar: enviarCanopus,
        contato_id: contato?.id,
      }, token, setAndamento);
      setResultado(r);
      // Guarda no contato o que só a Canopus pede, para a próxima venda já vir pronta.
      if (contato) {
        const detalhes = getContactDetails(contato);
        await dataProvider.update('contacts', {
          id: contato.id,
          data: { details: { ...detalhes, canopus: extrasParaContato(cliente) } },
          previousData: contato,
        }).catch(() => notify('Venda concluída. Não foi possível salvar os dados Canopus no contato.', { type: 'warning' }));
      }
    } catch (e: any) {
      setErro(e?.message || 'A venda Canopus falhou.');
    } finally {
      setEnviando(false); setConfirmando(false);
    }
  };

  const fechar = () => { if (!enviando) aoFechar(); };

  return (
    <Dialog open={!!plano} onOpenChange={(aberto) => !aberto && fechar()}>
      <DialogContent className="max-h-[92vh] max-w-[calc(100%-1.5rem)] overflow-y-auto sm:max-w-[760px]">
        <DialogHeader>
          <DialogTitle>Vender Canopus — {plano.codigo_bem}</DialogTitle>
          <DialogDescription>
            {plano.nome_bem} · {formatBRL(Number(plano.valor_credito))} · {plano.prazo_meses} meses · 1ª parcela {formatBRL(Number(plano.primeira_parcela))} · {plano.tipo_venda}
          </DialogDescription>
        </DialogHeader>

        {resultado ? (
          <div className="space-y-2 rounded-md border p-4 text-sm">
            <p className="font-semibold">{resultado.finalizado ? 'Venda enviada à Canopus' : resultado.pendencia ? 'Venda salva com pendência' : 'Pedido salvo na Canopus (não enviado)'}</p>
            <p>Pedido <b>{resultado.id_pedido}</b> · cliente {resultado.id_cliente} · grupo {resultado.grupo} · {resultado.prazo} meses · 1ª parcela {formatBRL(resultado.primeira_parcela)}</p>
            {resultado.pendencia && <p className="text-alerta">Pendência: {resultado.pendencia}</p>}
            {resultado.finalizado && <p>O cliente recebe contrato e boleto no e-mail {cliente?.email}.</p>}
            {!resultado.finalizado && <p className="text-muted-foreground">Envio posterior pelo botão “Enviar à Canopus”, em Canopus → Vendas.</p>}
            {resultado.registrada === false && <p className="text-alerta">Venda concluída na Canopus. Não foi possível registrá-la na North; informe o número do pedido ao suporte.</p>}
          </div>
        ) : (
          <div className="space-y-5">
            {!contato ? (
              <div className="space-y-2">
                <Label>Contato</Label>
                <Input placeholder="Buscar por nome (mínimo de 2 letras)" value={busca} onChange={(e) => setBusca(e.target.value)} />
                <div className="max-h-56 overflow-y-auto rounded border">
                  {(contatos || []).map((c) => (
                    <button key={c.id} type="button" className="block w-full px-3 py-2 text-left text-sm hover:bg-muted" onClick={() => escolher(c)}>
                      {getContactDisplayName(c) || `Contato ${c.id}`}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between rounded border px-3 py-2 text-sm">
                <span>Contato: <b>{getContactDisplayName(contato)}</b></span>
                <Button variant="ghost" size="sm" disabled={enviando} onClick={() => { setContato(null); setCliente(null); }}>Trocar</Button>
              </div>
            )}

            {cliente && (
              <div className="grid gap-x-4 gap-y-1 rounded-md bg-muted/40 p-3 text-xs sm:grid-cols-2">
                <span>CPF: <b>{cliente.cpf || '—'}</b></span>
                <span>Nascimento: <b>{cliente.nascimento || '—'}</b></span>
                <span>E-mail: <b>{cliente.email || '—'}</b></span>
                <span>Celular: <b>{cliente.celular || '—'}</b></span>
                <span>CEP: <b>{cliente.cep || '—'}</b>{cliente.numero ? `, nº ${cliente.numero}` : ''}</span>
                <span>Renda: <b>{cliente.renda || '—'}</b></span>
                <span>Estado civil: <b>{cliente.estado_civil || '—'}</b></span>
                <span>Profissão: <b>{cliente.profissao || '—'}</b></span>
              </div>
            )}

            {cliente && (pedir.size > 0 || revisarTudo) && (
              <section className="space-y-3 rounded-md border border-amber-300 p-3">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold">
                    {revisarTudo ? 'Todos os dados do cliente' : `Informações complementares: ${pedir.size} item(ns) pendente(s) na ficha da Canopus`}
                  </h3>
                  {!revisarTudo && <Button variant="ghost" size="sm" onClick={() => setRevisarTudo(true)}>Revisar todos os dados</Button>}
                </div>
                <CanopusClienteCampos cliente={cliente} aoMudar={setCliente} mostrar={(k) => revisarTudo || pedir.has(k)} />
              </section>
            )}

            {cliente && pedir.size === 0 && !revisarTudo && (
              <div className="flex items-center justify-between rounded-md border border-green-300 p-3 text-sm">
                <span>Ficha completa para a Canopus (dados da venda anterior deste contato).</span>
                <Button variant="ghost" size="sm" onClick={() => setRevisarTudo(true)}>Revisar dados</Button>
              </div>
            )}

            {cliente && (
              <section className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label className="text-xs">Forma de contato da venda</Label>
                  <Select value={formaContato} onValueChange={setFormaContato}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{FORMAS_CONTATO.map((f) => <SelectItem key={f.valor} value={String(f.valor)}>{f.rotulo}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Comprovante de renda</Label>
                  <Select value={comprovante} onValueChange={setComprovante}>
                    <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>{COMPROVANTES_RENDA.map((f) => <SelectItem key={f.valor} value={String(f.valor)}>{f.rotulo}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </section>
            )}

            {cliente && (
              <label className="flex items-start gap-2 rounded-md border border-red-300 p-3 text-sm">
                <Checkbox checked={enviarCanopus} onCheckedChange={(m) => setEnviarCanopus(m === true)} disabled={enviando} />
                <span>
                  <b>Enviar a venda à Canopus agora.</b> O cliente recebe contrato e boleto por e-mail. Desmarcado, o pedido fica
                  salvo como rascunho na Canopus.
                </span>
              </label>
            )}

            {pendentes.length > 0 && cliente && (
              <p className="text-xs text-alerta">Falta: {pendentes.join(', ')}.</p>
            )}
            {andamento && enviando && <p className="flex items-center gap-2 text-sm"><Spinner className="h-4 w-4" />{andamento}</p>}
            {erro && <p className="text-sm text-erro">{erro}</p>}
          </div>
        )}

        {confirmando && !resultado && cliente && (
          <div className="rounded-md border border-red-400 bg-red-50 p-3 text-sm dark:bg-red-950/30">
            Confirma enviar à Canopus a venda de <b>{plano.codigo_bem} ({formatBRL(Number(plano.valor_credito))}, {plano.prazo_meses} meses)</b> para{' '}
            <b>{cliente.nome}</b> (CPF {cliente.cpf})? Contrato e boleto vão para <b>{cliente.email}</b>.
          </div>
        )}

        <DialogFooter>
          {resultado ? (
            <Button onClick={aoFechar}>Fechar</Button>
          ) : (
            <>
              <Button variant="outline" onClick={fechar} disabled={enviando}>Cancelar</Button>
              {enviarCanopus && !confirmando ? (
                <Button variant="destructive" disabled={pendentes.length > 0 || enviando} onClick={() => setConfirmando(true)}>Revisar e enviar</Button>
              ) : (
                <Button
                  variant={enviarCanopus ? 'destructive' : 'default'}
                  disabled={pendentes.length > 0 || enviando}
                  onClick={enviar}
                >
                  {enviando ? 'Processando…' : enviarCanopus ? 'Confirmar envio à Canopus' : 'Salvar pedido (sem enviar)'}
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
